import type { ThreadNode } from "./threads";
import { flattenThread } from "./threads";
import { replyCount } from "./reviewSort";

/**
 * Collapsible reply threads (review-notes enhancements §2). Replies collapse by
 * DEFAULT — the root note is the signal; replies are supporting discussion that
 * add vertical noise to a dense rail, so the rail stays scannable and the sort
 * (§1) legible. A `Thread (N) ›` toggle above the replies always shows the count
 * so a busy thread is discoverable.
 *
 * The one override is a DEEP-LINK (§2.3): when a permalink targets a reply INSIDE
 * this thread, the thread must open so the reveal/scroll lands — a collapsed
 * thread must never swallow a deep-linked reply.
 */

export { replyCount };

/**
 * The initial expanded state for a root's reply subtree. Collapsed by default;
 * expanded when `revealCommentId` names any comment within the thread (the
 * deep-link auto-expand seam).
 */
export function defaultThreadExpanded(
  node: ThreadNode,
  revealCommentId?: number | null,
): boolean {
  if (revealCommentId == null) return false;
  return flattenThread(node).some((c) => c.id === revealCommentId);
}

/** The `Thread (N)` toggle label — pluralized reply count. */
export function threadToggleLabel(count: number): string {
  return `Thread (${count})`;
}

/** The accessible label for the toggle button (state + count). */
export function threadToggleAriaLabel(
  expanded: boolean,
  count: number,
): string {
  const noun = count === 1 ? "reply" : "replies";
  return expanded ? `Collapse ${count} ${noun}` : `Show ${count} ${noun}`;
}
